// Copyright (c) 2020 WiseTime. All rights reserved.

import { store } from "../store"
import * as UserContextActions from "../../features/user-context/actions"
import * as Grpc from "grpc-web"
import { BehaviorSubject, from as fromPromise, Observable, of } from "rxjs"
import { getBaseUrl } from "../routing"
import { RetryPolicy, withExponentialDelay } from "grpc-web-rx"
import { redirectToLogin } from "../../lib/rpcutils"
import { isDevelopmentEnv } from "../config"
import { accessTokenInFlight } from "../../App"

const tokenExchangeMetadata = (): RequestInit =>
  isDevelopmentEnv() ?
    ({
      method: "POST",
      // https://github.com/github/fetch#sending-cookies
      // tell the browser to include cookies (_refresh, _id) in requests
      credentials: "include",
    } as RequestInit) : ({ method: "POST" } as RequestInit)

/**
 * Attempts to exchange a _refresh cookie for an _id cookie (both cookies are HTTP-only), this is the default "before
 * retry" action for most rpc's retry policies.
 *
 * window.fetch is polyfilled via whatwg-fetch to cover older browsers e.g. IE11 and will not affect the native
 * implementations of window.fetch in modern browsers.
 *
 * Sample refresh to id token request and response:
 *
 * Request:
 * POST /customer/token
 * Cookie: _refresh=xxx
 *
 * Response:
 * HTTP 200 OK
 * Set-Cookie: _id=xxx
 * Body: {"idToken": "xxx"}
 */
export const tokenExchange = (
  onSuccess: () => void,
  wait: BehaviorSubject<boolean> = accessTokenInFlight,
  onRedirectToLogin = redirectToLogin,
): Promise<void> => {
  wait.next(true)
  return window.fetch(`${getBaseUrl()}/customer/token`, tokenExchangeMetadata())
    .then(response => {
      if (response.ok) {
        wait.next(false)
        onSuccess()
      } else {
        return Promise.reject({
          statusCode: response.status,
          message: response.statusText
        })
      }
    })
    .catch(error => {
      /**
       * onRedirectToLogin only in case of 403 status, otherwise,
       * source call that triggered token exchange will be retried with backoff.
       */
      if (error.statusCode === 403) {
        onRedirectToLogin()
      }
    })
    .finally(() => wait.next(false))
}

export const refreshTokenExchange = (
  onSuccess = () => { store.dispatch(UserContextActions.fetchUserDetails()) },
  wait: BehaviorSubject<boolean> = accessTokenInFlight,
): Observable<void> => fromPromise(tokenExchange(onSuccess, wait))

/**
 * Since gRPC-web does not properly map ESP v2 errors into their equivalent grpcWeb.StatusCode, we need to manually
 * remap HTTP 401 (from ESP) errors to (gRPC-web) StatusCode.UNAUTHENTICATED via envoy lua config.
 *
 * See https://github.com/grpc/grpc-web/issues/1023
 *
 * Status codes and their use in gRPC
 * https://chromium.googlesource.com/external/github.com/grpc/grpc/+/refs/tags/v1.21.4-pre1/doc/statuscodes.md
 */
export const isAuthError = (error: Grpc.RpcError): boolean =>
  error.code === Grpc.StatusCode.UNAUTHENTICATED

export const retryPolicyConfig = {
  maxRetries: 1,
  initialDelay: 500,
  maxDelay: 60_000,
}

/**
 * Retry policy that fetches user details (and update the store) after a successful token exchange.
 * If fails with 403 status it will redirect to login page.
 * This is the default retry policy for most gRPC calls, {@see grpc}.
 */
type RefreshExpiredTokenConfig = {
  maxRetries?: number,
  wait?: BehaviorSubject<boolean>,
  shouldRetry?: (error: Grpc.RpcError) => boolean,
  onSuccess?: () => void,
}
export const refreshExpiredToken = ({
  maxRetries = retryPolicyConfig.maxRetries,
  wait = accessTokenInFlight,
  shouldRetry = isAuthError,
  onSuccess = () => { store.dispatch(UserContextActions.fetchUserDetails()) },
}: RefreshExpiredTokenConfig = {}): RetryPolicy => {
  const retryAction = (error: Grpc.RpcError) => {
    if (wait.value || error.code !== Grpc.StatusCode.UNAUTHENTICATED) {
      // token exchange request is in flight or error is not due to expired token
      return of(undefined)
    }
    return refreshTokenExchange(onSuccess, wait)
  }
  return ({
    shouldRetry,
    maxRetries,
    beforeRetry: withExponentialDelay<void>(
      retryPolicyConfig.initialDelay,
      retryPolicyConfig.maxDelay,
    )(retryAction),
  })
}
